import { HStack, Modal, VStack } from '@musinsa/mwms-ds'
import Text from '@/components/content/Text'
import { colors, colorSystem } from '@/styles/theme'
import styled, { css } from 'styled-components'
import { Radio } from '@/components/content/Radio'
import { useSetAllocationPriority } from '@/services/outboundSchedule/useOutboundScheduleQueries'
import { CutType, GetOutboundSchedulesRes, SetAllocationPriorityReq } from '@/services/outboundSchedule/types'
import { AxiosError } from 'axios'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'
import MToast from '@/utils/modal/MToast'
import { Divider } from '@/components/content/Divider'
import { Controller, useForm } from 'react-hook-form'
import { AllocationPriorityForm, allocationPriorityFormSchema } from '../../schemas/priorityFormSchema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAllocationWarehouseOptions } from '../../hooks/useAllocationWarehouseOptions'
import { PrioritySelectors } from './PrioritySelectors'
import { useCutOffDate } from '../../hooks/useCutOffDate'
import { FieldItem } from '@/components/content/FieldItem'

type Props = {
  date: string
  time: string
  cutType: CutType
  onClose: () => void
  schedule: GetOutboundSchedulesRes['timeSlots'][number]['schedules'][number]
}

const getDefaultValues = (schedule: GetOutboundSchedulesRes['timeSlots'][number]['schedules'][number]): AllocationPriorityForm => {
  const { allocationStrategy, centerSchedules } = schedule

  const warehouseCodesArray = centerSchedules
    .filter((centerSchedule) => centerSchedule.priority > 0)
    .reduce<(string | undefined)[]>((acc, centerSchedule) => {
      acc[centerSchedule.priority - 1] = centerSchedule.warehouseCode
      return acc
    }, [])

  const hasPriority = centerSchedules.some((centerSchedule) => centerSchedule.priority > 0)

  return {
    allocationStrategy,
    packingWarehouseCodes: allocationStrategy === 'PACKING' ? [...warehouseCodesArray] : [],
    warehousePriorityWarehouseCodes: allocationStrategy === 'WAREHOUSE_PRIORITY' ? [...warehouseCodesArray] : [],
    usePriority: allocationStrategy === 'WAREHOUSE_PRIORITY' || hasPriority,
  }
}

export function AllocationPriorityModal({ date, time, cutType, schedule, onClose }: Props) {
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { isValid },
  } = useForm<AllocationPriorityForm>({
    resolver: zodResolver(allocationPriorityFormSchema),
    defaultValues: getDefaultValues(schedule),
  })

  const { shipperCode } = useCutOffDate()
  const warehouseOptions = useAllocationWarehouseOptions({ time, cutType })

  const usePriority = watch('usePriority') ?? false

  const setAllocationPriority = useSetAllocationPriority()

  const handleFormSubmit = async () => {
    const { allocationStrategy, packingWarehouseCodes = [], warehousePriorityWarehouseCodes = [], usePriority = false } = getValues()

    const warehouseCodes =
      allocationStrategy === 'WAREHOUSE_PRIORITY'
        ? warehousePriorityWarehouseCodes
        : allocationStrategy === 'PACKING' && usePriority
          ? packingWarehouseCodes
          : []

    const shouldHaveCenterPriority = allocationStrategy === 'WAREHOUSE_PRIORITY' || (allocationStrategy === 'PACKING' && usePriority)

    const validWarehouseCodes = warehouseCodes.filter(Boolean) as string[]
    const warehousePriorities = shouldHaveCenterPriority
      ? validWarehouseCodes.map((code, idx) => ({
          warehouseCode: code,
          priority: idx + 1,
        }))
      : []

    const payload: SetAllocationPriorityReq = {
      date,
      time,
      cutType,
      allocationStrategy,
      warehousePriorities,
      shipperCode,
    }

    try {
      await setAllocationPriority.mutateAsync(payload)

      MToast.show('우선순위가 저장되었습니다.')
    } catch (e) {
      MAlert.show(Utils.getApiErrMessage(e as AxiosError<ApiResponseV2>, '우선순위 설정에 실패했습니다.'))
    }

    onClose()
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <HStack gap={8} align="center">
          <Text kind="Headline_20_bold">우선순위 설정</Text>
          <Text kind="Headline_16_regular" color={colorSystem.text.common.secondary}>
            [{cutType}] {time}
          </Text>
        </HStack>
      }
      buttons={[
        { kind: 'secondary', content: '취소', onClick: onClose },
        { kind: 'primary_light', content: '저장', onClick: handleFormSubmit, disabled: !isValid },
      ]}
    >
      <VStack gap={16}>
        <Text kind="Headline_16_bold">우선순위 기준</Text>
        <Controller
          control={control}
          name="allocationStrategy"
          render={({ field }) => (
            <HStack gap={16}>
              <PriorityItem
                $isSelected={field.value === 'PACKING'}
                onClick={() => {
                  if (cutType === 'B2B') {
                    setValue('usePriority', false)
                  }
                  field.onChange('PACKING')
                }}
              >
                <Radio
                  value="PACKING"
                  checked={field.value === 'PACKING'}
                  onChange={() => {
                    if (cutType === 'B2B') {
                      setValue('usePriority', false)
                    }
                    field.onChange('PACKING')
                  }}
                />
                <VStack gap={4} style={{ padding: '2px 0' }}>
                  <Text kind="Body_14_medium">합포 순</Text>
                  <Text kind="Body_12_regular" color={colorSystem.text.common.tertiary}>
                    합포 많은 순
                  </Text>
                </VStack>
              </PriorityItem>

              <PriorityItem
                $isSelected={field.value === 'WAREHOUSE_PRIORITY'}
                onClick={() => {
                  field.onChange('WAREHOUSE_PRIORITY')
                }}
              >
                <Radio
                  value="WAREHOUSE_PRIORITY"
                  checked={field.value === 'WAREHOUSE_PRIORITY'}
                  onChange={() => {
                    field.onChange('WAREHOUSE_PRIORITY')
                  }}
                />
                <VStack gap={4} style={{ padding: '2px 0' }}>
                  <Text kind="Body_14_medium">센터 순</Text>
                  <Text kind="Body_12_regular" color={colorSystem.text.common.tertiary}>
                    우선순위 높은 센터 순
                  </Text>
                </VStack>
              </PriorityItem>
            </HStack>
          )}
        />

        {watch('allocationStrategy') === 'PACKING' && (
          <>
            <Divider />

            <VStack gap={16}>
              <VStack gap={4}>
                <Text kind="Headline_16_regular" style={{ fontWeight: 500 }}>
                  합포 조건 내, 센터 우선순위
                </Text>
                <Text kind="Body_12_regular" color={colorSystem.text.common.secondary}>
                  동일한 합포 조건을 여러 센터가 가진 경우, 센터 우선순위에 따라 출고됩니다.
                  <br />
                  해당 컷오프 시간을 운영하지 않는 센터는 우선순위를 지정할 수 없습니다.
                </Text>
              </VStack>
              <FieldItem
                label={
                  <Text color={colorSystem.text.common.primary} kind="Body_14_regular">
                    우선순위 사용여부
                  </Text>
                }
                labelWidth={140}
                required
                requiredColor={colorSystem.text.common.highlight}
              >
                <Controller
                  control={control}
                  name="usePriority"
                  render={({ field }) => (
                    <VStack gap={4}>
                      <HStack gap={32}>
                        <Radio
                          value="true"
                          disabled={cutType === 'B2B'}
                          checked={field.value === true}
                          onChange={() => field.onChange(true)}
                          label="사용"
                        />
                        <Radio
                          value="false"
                          disabled={cutType === 'B2B'}
                          checked={field.value === false}
                          onChange={() => field.onChange(false)}
                          label="미사용"
                        />
                      </HStack>
                      {cutType === 'B2B' && (
                        <Text kind="Body_12_regular" color={colorSystem.text.common.tertiary}>
                          B2B 출고는 합포 내, 우선순위를 사용하지 않습니다.
                        </Text>
                      )}
                    </VStack>
                  )}
                />
              </FieldItem>
              {usePriority && (
                <PrioritySelectors
                  warehouseCodes={watch('packingWarehouseCodes')}
                  warehouseOptions={warehouseOptions}
                  type="ALLOCATION"
                  onWarehouseChange={(idx, value) => setValue(`packingWarehouseCodes.${idx}`, value, { shouldValidate: true })}
                />
              )}
            </VStack>
          </>
        )}

        {/* only 센터 순 */}
        {watch('allocationStrategy') === 'WAREHOUSE_PRIORITY' && (
          <>
            <Divider />

            <VStack gap={16}>
              <VStack gap={4}>
                <Text kind="Headline_16_regular" style={{ fontWeight: 500 }}>
                  센터 우선 순위
                </Text>
                <Text kind="Body_12_regular" color={colorSystem.text.common.secondary}>
                  최대 5순위까지 지정할 수 있으며, 이후 순서는 합포 기준으로 출고됩니다.
                  <br />
                  해당 컷오프 시간을 운영하지 않는 센터는 우선순위를 지정할 수 없습니다.
                </Text>
              </VStack>

              <PrioritySelectors
                warehouseCodes={watch('warehousePriorityWarehouseCodes')}
                warehouseOptions={warehouseOptions}
                type="ALLOCATION"
                onWarehouseChange={(idx, value) => setValue(`warehousePriorityWarehouseCodes.${idx}`, value, { shouldValidate: true })}
              />
            </VStack>
          </>
        )}
      </VStack>
    </Modal>
  )
}

const PriorityItem = styled.div<{ $isSelected: boolean }>`
  flex: 1;
  padding: 16px;
  border-radius: 8px;
  outline: 1px solid ${colorSystem.border.element};
  cursor: pointer;

  ${({ $isSelected }) =>
    $isSelected
      ? css`
          outline: 2px solid ${colors.blue500};
        `
      : css`
          &:hover {
            background-color: ${colors.gray050};
          }
        `}

  display: flex;
  align-items: flex-start;
  gap: 8px;
`
